import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AuthSessionProvider,
  useAuthSession,
} from "../features/auth/shared/hooks/auth-session-context";
import { AUTH_SESSION_EXPIRED_EVENT } from "../lib/authTokenService";
import { showToast } from "../components/toast-store";
import { useLocalization } from "../hooks/useLocalization";
import { clearDashboardCache } from "../features/dashboard/utils/dashboardCache";
import { setThemeMode } from "../utils/theme.utlis";
import { isMobileOrTabletDevice } from "../utils/device";
import { isAnonymousPublicPath, isPasswordResetPath } from "./routes/appRouteMatchers";
import { BaseUserResponse } from "../features/auth/data";

interface AppThemeContextValue {
  darkMode: boolean;
  setDarkMode: (value: boolean) => void;
  onToggleDarkMode: () => void;
}

export type AppFeatureKey =
  | "admin_dashboard"
  | "merchant_dashboard"
  | "dev_playground"
  | "qr_scanner"
  | "notifications"
  | "recent_activities";

export type AppFeatureFlags = Record<AppFeatureKey, boolean>;

const DEFAULT_FEATURE_FLAGS: AppFeatureFlags = {
  admin_dashboard: true,
  merchant_dashboard: true,
  dev_playground: true,
  qr_scanner: true,
  notifications: true,
  recent_activities: true,
};

interface AppFeatureContextValue {
  featureFlags: AppFeatureFlags;
  isFeatureEnabled: (key: AppFeatureKey) => boolean;
  setFeatureEnabled: (key: AppFeatureKey, enabled: boolean) => void;
  setFeatureFlags: (flags: Partial<AppFeatureFlags>) => void;
}

interface AppContextValue {
  currentLanguage: string;
  changeLanguage: (language: string) => void;
  isInitialized: boolean;
  isLoggedIn: boolean;
  isMobileOrTablet: boolean;
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  showActivities: boolean;
  setShowActivities: (show: boolean) => void;
  showLogoutDialog: boolean;
  setShowLogoutDialog: (show: boolean) => void;
  onLogout: () => void;
  user: BaseUserResponse | null;
}

const AppThemeContext = createContext<AppThemeContextValue | undefined>(undefined);
const AppFeatureContext = createContext<AppFeatureContextValue | undefined>(undefined);
const AppContext = createContext<AppContextValue | undefined>(undefined);

export const useAppTheme = () => {
  const context = useContext(AppThemeContext);
  if (!context) {
    throw new Error("useAppTheme must be used within AppRootProviders");
  }
  return context;
};

/** Returns application feature flags and their update actions. */
export const useAppFeatures = () => {
  const context = useContext(AppFeatureContext);
  if (!context) {
    throw new Error("useAppFeatures must be used within AppRootProviders");
  }
  return context;
};

/** Returns cross-cutting app state such as auth, navigation, and shell UI. */
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within AppRootProviders");
  }
  return context;
};

interface AppContextProviderProps {
  children: React.ReactNode;
}

/** Coordinates app-level effects and exposes shell state to route content. */
const AppContextProvider: React.FC<AppContextProviderProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showActivities, setShowActivities] = useState(false);

  const { isLoggedIn, isInitialized, user, clearSession } = useAuthSession();
  const { initializeLanguage, changeLanguage, currentLanguage } = useLocalization();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobileOrTablet = isMobileOrTabletDevice();

  useEffect(() => {
    clearDashboardCache();
  }, []);

  useEffect(() => {
    initializeLanguage();
  }, [initializeLanguage]);

  useEffect(() => {
    const handleSessionExpired = (event: Event) => {
      const message =
        event instanceof CustomEvent && typeof event.detail?.message === "string"
          ? event.detail.message
          : null;

      if (message) {
        showToast({
          type: "warning",
          title: "Session expired",
          message,
        });
      }
      navigate("/login", { replace: true });
    };

    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, [navigate]);

  useEffect(() => {
    if (
      isPasswordResetPath(location.pathname) ||
      isAnonymousPublicPath(location.pathname, location.search)
    ) {
      return;
    }

    if (location.pathname.startsWith("/qr-scanner")) return;

    if (isMobileOrTablet) {
      navigate("/qr-scanner/welcome");
    }
  }, [isMobileOrTablet, location.pathname, location.search, navigate]);

  const onLogout = useCallback(() => {
    clearSession();
    document.documentElement.classList.remove("dark");
    navigate("/");
  }, [clearSession, navigate]);

  const appContextValue = useMemo<AppContextValue>(
    () => ({
      currentLanguage,
      changeLanguage,
      isInitialized,
      isLoggedIn,
      isMobileOrTablet,
      sidebarOpen,
      setSidebarOpen,
      showActivities,
      setShowActivities,
      showLogoutDialog,
      setShowLogoutDialog,
      onLogout,
      user,
    }),
    [
      currentLanguage,
      changeLanguage,
      isInitialized,
      isLoggedIn,
      isMobileOrTablet,
      onLogout,
      sidebarOpen,
      showActivities,
      showLogoutDialog,
      user,
    ],
  );

  return <AppContext.Provider value={appContextValue}>{children}</AppContext.Provider>;
};

interface AppRootProvidersProps {
  children: React.ReactNode;
}

/** Composes the providers required by the application root. */
const AppRootProviders: React.FC<AppRootProvidersProps> = ({ children }) => {
  const darkMode = false;
  const [featureFlags, setFeatureFlagsState] = useState<AppFeatureFlags>(DEFAULT_FEATURE_FLAGS);

  useEffect(() => {
    setThemeMode(false);
  }, []);

  const setDarkMode = useCallback((_value: boolean) => {
    setThemeMode(false);
  }, []);

  const onToggleDarkMode = useCallback(() => {
    setThemeMode(false);
  }, []);

  const isFeatureEnabled = useCallback((key: AppFeatureKey) => featureFlags[key], [featureFlags]);

  const setFeatureEnabled = useCallback((key: AppFeatureKey, enabled: boolean) => {
    setFeatureFlagsState((current) => ({
      ...current,
      [key]: enabled,
    }));
  }, []);

  const setFeatureFlags = useCallback((flags: Partial<AppFeatureFlags>) => {
    setFeatureFlagsState((current) => ({
      ...current,
      ...flags,
    }));
  }, []);

  const themeContextValue = useMemo<AppThemeContextValue>(
    () => ({
      darkMode,
      setDarkMode,
      onToggleDarkMode,
    }),
    [darkMode, onToggleDarkMode, setDarkMode],
  );

  const featureContextValue = useMemo<AppFeatureContextValue>(
    () => ({
      featureFlags,
      isFeatureEnabled,
      setFeatureEnabled,
      setFeatureFlags,
    }),
    [featureFlags, isFeatureEnabled, setFeatureEnabled, setFeatureFlags],
  );

  return (
    <AppThemeContext.Provider value={themeContextValue}>
      <AppFeatureContext.Provider value={featureContextValue}>
        <AuthSessionProvider>
          <AppContextProvider>{children}</AppContextProvider>
        </AuthSessionProvider>
      </AppFeatureContext.Provider>
    </AppThemeContext.Provider>
  );
};

export default AppRootProviders;
